import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { captureException } from "@sentry/react";
import { APP_STORAGE_VERSION, migrateAppStorage } from "./appStorageMigration";
import type { Locale } from "@/lib/i18n/config";
import type { PlaybackSpeed } from "@/types/player.type";
import { changeAppLanguage } from "@/lib/i18n";
import { isKnownLanguage } from "@/lib/player/trackLanguage";
import { apolloClient } from "@/api/apiClient";

interface EpisodeHistoryEntry {
  /** Episode to resume on — the local twin of `Show.progression.nextEpisode`. */
  nextEpisodeId: string;
}

export interface AppStoreState {
  soundMuted: boolean;
  /** BCP-47 code of the subtitle track the user picked, "off" when disabled, null → follow the app locale. */
  subtitlesPreference: string | "off" | null;
  // Note: `null` is set at boot and by the v7 migration only. No menu offers a
  // way back to it, so the locale fallback is lost once the user picks a track.
  /** BCP-47 code of the audio track the user picked; null → follow the app locale. */
  audioPreference: string | null;
  /** Set once the user dismisses the initial activation paywall; never resets. */
  initialPaywallDismissed: boolean;
  /** Playback rate picked in the new player's settings (SHO-815); carried across episodes and series. */
  playbackSpeed: PlaybackSpeed;
  history: Partial<Record<string, EpisodeHistoryEntry>>;
  /** User-selected UI locale; undefined → browser detection decides at boot. */
  locale?: Locale;
}

interface AppStore extends AppStoreState {
  setSoundMuted: (muted: boolean) => void;
  setEpisodeHistory: (showId: string, entry: EpisodeHistoryEntry) => void;
  setLocale: (locale: Locale) => void;
  setAudioPreference: (language: string) => void;
  /** The BCP-47 code of the track the user picked. Off is `disableSubtitles`. */
  setSubtitlesPreference: (language: string) => void;
  disableSubtitles: () => void;
  setPlaybackSpeed: (speed: PlaybackSpeed) => void;
}

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (set) => ({
        soundMuted: false,
        subtitlesPreference: null,
        audioPreference: null,
        initialPaywallDismissed: false,
        playbackSpeed: 1,
        history: {},
        locale: undefined,
        setSoundMuted: (muted) => set({ soundMuted: muted }),
        setEpisodeHistory: (showId, entry) =>
          set((s) => ({ history: { ...s.history, [showId]: entry } })),
        // A track the stream can't name ("" on native HLS, "und" without a
        // LANGUAGE attribute) is not a preference worth keeping: stored, it
        // matches nothing on the next stream and there is no UI to clear it,
        // which strands the user on the manifest default for good. null =
        // follow the locale, which is what we'd do anyway.
        setAudioPreference: (language) =>
          set({ audioPreference: isKnownLanguage(language) ? language : null }),
        setSubtitlesPreference: (language) =>
          set({ subtitlesPreference: language }),
        // The "off" sentinel lives here only, so a menu can't drift on its
        // casing — and can't reach for `null`, which means the opposite.
        disableSubtitles: () => set({ subtitlesPreference: "off" }),
        setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
        setLocale: (locale) => {
          // Persist only once the chunk resolved: writing first would leave
          // `app-storage` pointing at a locale the next boot can't load either.
          void changeAppLanguage(locale).then(
            () => {
              set({ locale });
              // The API localizes on `Accept-Language`, so everything cached
              // under the previous locale is stale. resetStore (not clearStore)
              // refetches the active queries with the new header. Its promise
              // rejects as soon as one of those refetches is torn down before
              // it delivered (the menu closing after the pick, a section
              // re-rendering in the new locale): Apollo's own `AbortError`,
              // which is nothing wrong (SHO-840). Each query surfaces its own
              // error through `useQuery`, so there is nothing to report here.
              void apolloClient.resetStore().catch(() => {});
            },
            // A chunk that can't load: the choice is not persisted, and that
            // is worth knowing.
            (error) => captureException(error),
          );
        },
      }),
      {
        name: "app-storage",
        version: APP_STORAGE_VERSION,
        migrate: migrateAppStorage,
      },
    ),
  ),
);
